import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { employeesApi, listsApi, analyticsApi, entraApi } from "../services/api";
import HelpModal from "../components/HelpModal";
import WhatsNewBell from "../components/WhatsNewBell";

// Validation helpers
const validateEmail = (email) => {
  if (!email) return true;
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

const validatePhone = (phone) => {
  if (!phone) return true;
  const re = /^[\d\s\-\(\)\+\.]+$/;
  return re.test(phone);
};

const validateDate = (date) => {
  if (!date) return true;
  const re = /^\d{4}-\d{2}-\d{2}$|^\d{2}\/\d{2}\/\d{4}$/;
  return re.test(date);
};

const validateNoSpecialChars = (value) => {
  if (!value) return true;
  const re = /^[a-zA-Z0-9\s]*$/;
  return re.test(value);
};

const validateLicenseState = (value) => {
  if (!value) return true;
  // Must be exactly 2 uppercase letters (US state abbreviation)
  const re = /^[A-Z]{2}$/;
  return re.test(value.toUpperCase());
};

// US State abbreviations for validation reference
const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC"
];

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    return date.toISOString().split("T")[0];
  } catch {
    return dateStr;
  }
};

const MainPage = () => {
  const { user, logout, isAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lists, setLists] = useState({});
  const [myStats, setMyStats] = useState(null);
  const [showFacilityPicker, setShowFacilityPicker] = useState(false);
  const [showJobTitlePicker, setShowJobTitlePicker] = useState(false);
  const [showHomeFacilityPicker, setShowHomeFacilityPicker] = useState(false);
  const [jobTitleSearch, setJobTitleSearch] = useState("");
  const [homeFacilitySearch, setHomeFacilitySearch] = useState("");
  const [additionalFacilitySearch, setAdditionalFacilitySearch] = useState("");
  const [locks, setLocks] = useState([]);
  const [lockError, setLockError] = useState(null);
  const [dorMembers, setDorMembers] = useState([]);
  const [dorLoading, setDorLoading] = useState(true);
  const [dorError, setDorError] = useState(null);
  const [showDorPicker, setShowDorPicker] = useState(false);
  const [dorSearch, setDorSearch] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [allUsersLoading, setAllUsersLoading] = useState(true);
  const [allUsersError, setAllUsersError] = useState(null);
  const [showManagerPicker, setShowManagerPicker] = useState(false);
  const [managerSearch, setManagerSearch] = useState("");
  const facilityPickerRef = useRef(null);
  const jobTitlePickerRef = useRef(null);
  const homeFacilityPickerRef = useRef(null);
  const jobTitleSearchRef = useRef(null);
  const homeFacilitySearchRef = useRef(null);
  const dorPickerRef = useRef(null);
  const dorSearchRef = useRef(null);
  const managerPickerRef = useRef(null);
  const managerSearchRef = useRef(null);

  // Tab state
  const [mainTab, setMainTab] = useState("process"); // "process" | "statusChanges" | "processed"

  // Processed Employees tab state
  const [processedList, setProcessedList] = useState([]);
  const [processedSearch, setProcessedSearch] = useState("");
  const [viewingProcessedEmp, setViewingProcessedEmp] = useState(null);
  const [revealPassword, setRevealPassword] = useState(false);

  // Status Changes state
  const [entraUser, setEntraUser] = useState(null);
  const [entraSearchQuery, setEntraSearchQuery] = useState("");
  const [entraEditMode, setEntraEditMode] = useState(false);
  const [entraLoading, setEntraLoading] = useState(false);
  const [entraAdditionalEmails, setEntraAdditionalEmails] = useState([]);
  const [entraNewAdditionalEmail, setEntraNewAdditionalEmail] = useState("");
  const [showHelp, setShowHelp] = useState(false);
  const [entraFacilitySearch, setEntraFacilitySearch] = useState("");
  const [showEntraFacilityPicker, setShowEntraFacilityPicker] = useState(false);
  const entraFacilityPickerRef = useRef(null);
  const [entraEditData, setEntraEditData] = useState({
    accountEnabled: true, employeeType: "", jobTitle: "", officeLocation: "",
    businessPhones: [], mobilePhone: "", personalEmail: "",
    facilityName: "", managerEmail: "", startDate: ""
  });

  // Close pickers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (facilityPickerRef.current && !facilityPickerRef.current.contains(event.target)) {
        setShowFacilityPicker(false);
        setAdditionalFacilitySearch("");
      }
      if (jobTitlePickerRef.current && !jobTitlePickerRef.current.contains(event.target)) {
        setShowJobTitlePicker(false);
        setJobTitleSearch("");
      }
      if (homeFacilityPickerRef.current && !homeFacilityPickerRef.current.contains(event.target)) {
        setShowHomeFacilityPicker(false);
        setHomeFacilitySearch("");
      }
      if (dorPickerRef.current && !dorPickerRef.current.contains(event.target)) {
        setShowDorPicker(false);
        setDorSearch("");
      }
      if (managerPickerRef.current && !managerPickerRef.current.contains(event.target)) {
        setShowManagerPicker(false);
        setManagerSearch("");
      }
      if (entraFacilityPickerRef.current && !entraFacilityPickerRef.current.contains(event.target)) {
        setShowEntraFacilityPicker(false);
        setEntraFacilitySearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Focus search input when dropdowns open
  useEffect(() => {
    if (showJobTitlePicker && jobTitleSearchRef.current) {
      jobTitleSearchRef.current.focus();
    }
  }, [showJobTitlePicker]);

  useEffect(() => {
    if (showHomeFacilityPicker && homeFacilitySearchRef.current) {
      homeFacilitySearchRef.current.focus();
    }
  }, [showHomeFacilityPicker]);

  useEffect(() => {
    if (showDorPicker && dorSearchRef.current) {
      dorSearchRef.current.focus();
    }
  }, [showDorPicker]);

  useEffect(() => {
    if (showManagerPicker && managerSearchRef.current) {
      managerSearchRef.current.focus();
    }
  }, [showManagerPicker]);

  // Fetch processed employees list when the Processed Employees tab is opened.
  // Refetched each visit so the list reflects callbacks since last view.
  useEffect(() => {
    if (mainTab !== "processed") return;
    let cancelled = false;
    (async () => {
      try {
        const data = await employeesApi.getProcessed();
        if (!cancelled) setProcessedList(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch processed employees:", err);
        if (!cancelled) setProcessedList([]);
      }
    })();
    return () => { cancelled = true; };
  }, [mainTab]);

  // Fetch DOR group members from Entra ID
  useEffect(() => {
    const fetchDorMembers = async () => {
      setDorLoading(true);
      setDorError(null);
      try {
        const members = await entraApi.getGroupMembers("50c3d925-42dd-42b3-ac60-2d0ce5880022");
        setDorMembers(members);
      } catch (err) {
        const detail = err.response?.data?.detail || err.message || "Unknown error";
        console.error("Failed to fetch DOR group members:", detail);
        setDorError(detail);
      } finally {
        setDorLoading(false);
      }
    };
    fetchDorMembers();
  }, []);

  // Fetch all company users from Entra ID for Manager lookup
  useEffect(() => {
    const fetchAllUsers = async () => {
      setAllUsersLoading(true);
      setAllUsersError(null);
      try {
        const users = await entraApi.getAllUsers();
        setAllUsers(users);
      } catch (err) {
        const detail = err.response?.data?.detail || err.message || "Unknown error";
        console.error("Failed to fetch all users:", detail);
        setAllUsersError(detail);
      } finally {
        setAllUsersLoading(false);
      }
    };
    fetchAllUsers();
  }, []);

  // Release locks when leaving the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Use sendBeacon for reliable delivery during page unload
      const token = localStorage.getItem('hr_token');
      if (token) {
        navigator.sendBeacon(
          `${process.env.REACT_APP_BACKEND_URL}/api/employees/unlock-all-mine`,
          new Blob([JSON.stringify({})], { type: 'application/json' })
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Also try to unlock when component unmounts
      if (selectedEmployee) {
        employeesApi.unlock(selectedEmployee.id).catch(() => {});
      }
    };
  }, [selectedEmployee]);

  // Required fields (removed license fields)
  const requiredFields = [
    "position_id", "position_start_date", "pay_group", "employee_status",
    "first_name", "last_name", "personal_email", "home_phone_number",
    "birth_date", "worked_state_tax_code", "employee_type",
    "home_facility", "roles", "manager_email",
    "director_of_rehabilitation_email", "facility_email", "ops_admin",
    "clock_in_out", "adp_code", "discipline", "designation", "is_a_tcn",
    "license_state", "license_number", "license_issue_date", "license_expiration",
  ];

  // Fields that are displayed on the form and should be validated
  // Note: job_title is excluded from validation - allows special characters
  const displayedFields = [
    "position_id", "position_start_date", "pay_group", "employee_status",
    "first_name", "middle_name", "last_name", "personal_email", "home_phone_number",
    "birth_date", "worked_state_tax_code", "employee_type",
    "home_facility", "facility_id", "roles", "manager_email", "license_state", "license_number",
    "license_issue_date", "license_expiration", "director_of_rehabilitation_email", 
    "facility_email", "ops_admin", "clock_in_out", "adp_code", "discipline", 
    "designation", "is_a_tcn", "additional_facilities", "notes"
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [employeesData, listsData, statsData, locksData] = await Promise.all([
        employeesApi.getUnprocessed(),
        listsApi.getAll(),
        analyticsApi.getMyStats().catch(() => null),
        employeesApi.getLocks().catch(() => []),
      ]);
      setEmployees(employeesData);
      setMyStats(statsData);
      setLocks(locksData);

      const listsObj = {};
      listsData.forEach((list) => {
        listsObj[list.name] = list.items;
      });
      setLists(listsObj);
    } catch (err) {
      toast.error("Failed to load data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Helper to check if an employee is locked by someone else
  const getEmployeeLock = (employeeId) => {
    return locks.find(lock => lock.employee_id === employeeId && lock.locked_by !== user?.email);
  };

  const handleEmployeeSelect = async (e) => {
    const id = e.target.value;
    if (!id) {
      // Release previous lock if any
      if (selectedEmployee) {
        try {
          await employeesApi.unlock(selectedEmployee.id);
        } catch (err) {
          // Ignore unlock errors
        }
      }
      setSelectedEmployee(null);
      setFormData({});
      setErrors({});
      setTouched({});
      setLockError(null);
      return;
    }

    // Check if locked by someone else
    const existingLock = getEmployeeLock(id);
    if (existingLock) {
      setLockError(`This record is currently being edited by ${existingLock.locked_by}`);
      return;
    }

    try {
      // Release previous lock if switching employees
      if (selectedEmployee && selectedEmployee.id !== id) {
        try {
          await employeesApi.unlock(selectedEmployee.id);
        } catch (err) {
          // Ignore unlock errors
        }
      }

      // Try to lock the new employee
      await employeesApi.lock(id);
      setLockError(null);

      const employee = await employeesApi.getById(id);
      setSelectedEmployee(employee);
      // Apply defaults for empty fields
      const newFormData = { 
        ...employee, 
        hr_agent: user.email,
        roles: employee.roles || "Clinical user",
        employee_status: employee.employee_status || "Active"
      };
      setFormData(newFormData);
      
      // Mark all fields as touched so errors show immediately
      const allTouched = {};
      Object.keys(newFormData).forEach((f) => (allTouched[f] = true));
      requiredFields.forEach((f) => (allTouched[f] = true));
      setTouched(allTouched);
      
      // Validate all fields and set errors
      validateAllFields(newFormData);
      
      // Refresh locks list
      const locksData = await employeesApi.getLocks().catch(() => []);
      setLocks(locksData);
    } catch (err) {
      if (err.response?.status === 423) {
        // Record is locked by someone else
        setLockError(err.response.data.detail);
      } else {
        toast.error("Failed to load employee data");
      }
    }
  };

  // Create a new employee manually
  const handleAddNewEmployee = async () => {
    // Release any existing lock first
    if (selectedEmployee && !selectedEmployee.isNew) {
      try {
        await employeesApi.unlock(selectedEmployee.id);
      } catch (err) {
        // Ignore errors
      }
    }
    
    // Generate a new unique ID
    const newId = `NEW-${Date.now()}`;
    
    // Create a blank employee object
    const newEmployee = {
      id: newId,
      isNew: true, // Flag to indicate this is a new record
      position_id: "",
      position_start_date: "",
      pay_group: "",
      employee_status: "Active",
      first_name: "",
      middle_name: "",
      last_name: "",
      personal_email: "",
      home_phone_number: "",
      birth_date: "",
      worked_state_tax_code: "",
      job_title: "",
      employee_type: "",
      home_facility: "",
      roles: "Clinical user",
      additional_facilities: "",
      facility_email: "",
      adp_code: "",
      license_state: "",
      license_number: "",
      license_issue_date: "",
      license_expiration: "",
      discipline: "",
      designation: "",
      manager_email: "",
      director_of_rehabilitation_email: "",
      ops_admin: "",
      clock_in_out: "",
      is_a_tcn: "",
      hr_agent: user.email,
    };
    
    setSelectedEmployee(newEmployee);
    setFormData(newEmployee);
    setErrors({});
    setTouched({});
    setLockError(null);
  };

  // Release lock and clear selection
  const handleReleaseLock = async () => {
    if (selectedEmployee && !selectedEmployee.isNew) {
      try {
        await employeesApi.unlock(selectedEmployee.id);
      } catch (err) {
        // Ignore errors
      }
    }
    setSelectedEmployee(null);
    setFormData({});
    setErrors({});
    setTouched({});
    setLockError(null);
    fetchData(); // Refresh the locks list
  };

  const validateField = (field, value) => {
    // Only validate fields that are displayed on the form
    if (!displayedFields.includes(field)) {
      return null; // Skip validation for non-displayed fields
    }
    
    // Skip validation for system/metadata fields
    const systemFields = ["id", "hr_agent", "created_at", "updated_at", "_id"];
    if (systemFields.includes(field)) {
      return null;
    }
    
    // Treat whitespace-only as empty
    const trimmedValue = value ? value.toString().trim() : "";
    
    if (requiredFields.includes(field) && !trimmedValue) {
      return "Required";
    }
    if (field.includes("email") && trimmedValue && !validateEmail(trimmedValue)) {
      return "Invalid email";
    }
    if (field === "home_phone_number" && trimmedValue && !validatePhone(trimmedValue)) {
      return "Invalid phone";
    }
    // Skip date validation for system timestamp fields
    if ((field === "created_at" || field === "updated_at") && trimmedValue) {
      return null;
    }
    if (field.includes("date") && trimmedValue && !validateDate(trimmedValue)) {
      return "Invalid date";
    }
    // License expiration is a date field - skip special char validation
    if (field === "license_expiration" && trimmedValue) {
      return null; // Already validated as date above if needed
    }
    // Special validation for license_state - must be 2-char US state abbreviation (only if value provided)
    if (field === "license_state" && trimmedValue) {
      const upperValue = trimmedValue.toUpperCase();
      if (!validateLicenseState(upperValue)) {
        return "Must be 2-letter state code";
      }
      if (!US_STATES.includes(upperValue)) {
        return "Invalid US state";
      }
    }
    // Skip special char validation for certain fields
    const skipSpecialCharValidation = [
      "email", "phone", "date", "home_facility", "adp_code", "license_state", 
      "additional_facilities", "expiration", "license", "id", "notes", "roles"
    ];
    const shouldSkip = skipSpecialCharValidation.some(skip => field.includes(skip));
    if (!shouldSkip && trimmedValue && !validateNoSpecialChars(trimmedValue)) {
      return "No special characters";
    }
    return null;
  };

  const validateAllFields = (data) => {
    const newErrors = {};
    Object.keys(data).forEach((field) => {
      const error = validateField(field, data[field]);
      if (error) newErrors[field] = error;
    });
    requiredFields.forEach((field) => {
      if (!data[field]) newErrors[field] = "Required";
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    // Auto-uppercase license_state
    let processedValue = value;
    if (field === "license_state" && value) {
      processedValue = value.toUpperCase().slice(0, 2);
    }
    
    const newFormData = { ...formData, [field]: processedValue };
    setFormData(newFormData);
    setTouched((prev) => ({ ...prev, [field]: true }));

    const error = validateField(field, processedValue);
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[field] = error;
      } else {
        delete newErrors[field];
      }
      return newErrors;
    });
  };

  const isFormValid = () => {
    for (const field of requiredFields) {
      if (!formData[field]) return false;
    }
    return Object.keys(errors).length === 0;
  };

  // Save without submitting to Power Automate
  const handleSave = async () => {
    setSaving(true);
    try {
      if (selectedEmployee.isNew) {
        // Create new employee
        const dataToSave = { ...formData };
        // Use position_id as the ID if provided, otherwise generate one
        if (dataToSave.position_id) {
          dataToSave.id = dataToSave.position_id;
        } else {
          dataToSave.id = `MANUAL-${Date.now()}`;
        }
        delete dataToSave.isNew;
        await employeesApi.create(dataToSave);
        toast.success("New employee created!");
        // Update the selectedEmployee to reflect it's no longer new
        setSelectedEmployee({ ...selectedEmployee, id: dataToSave.id, isNew: false });
        fetchData(); // Refresh the list
      } else {
        // Update existing employee
        await employeesApi.update(selectedEmployee.id, formData);
        toast.success("Employee data saved!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    // Mark all fields as touched
    const allTouched = {};
    requiredFields.forEach((f) => (allTouched[f] = true));
    setTouched(allTouched);

    if (!validateAllFields(formData)) {
      toast.error("Please fill in all required fields correctly");
      return;
    }

    setSubmitting(true);
    try {
      let employeeId = selectedEmployee.id;
      
      // If this is a new employee, create it first
      if (selectedEmployee.isNew) {
        const dataToSave = { ...formData };
        // Use position_id as the ID if provided
        if (dataToSave.position_id) {
          dataToSave.id = dataToSave.position_id;
          employeeId = dataToSave.position_id;
        } else {
          dataToSave.id = `MANUAL-${Date.now()}`;
          employeeId = dataToSave.id;
        }
        delete dataToSave.isNew;
        await employeesApi.create(dataToSave);
      }
      
      // Now submit the employee
      const result = await employeesApi.submit(employeeId, formData);
      if (result.success) {
        if (result.pending) {
          toast.success("Employee submitted! Waiting for Power Automate confirmation...");
        } else {
          toast.success("Employee submitted successfully!");
        }
        setSelectedEmployee(null);
        setFormData({});
        setTouched({});
        fetchData();
      } else if (result.entra_duplicate) {
        // Entra ID duplicate detected
        toast.error(result.message, { duration: 8000 });
      } else {
        toast.error(result.message || "Submission failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle facility in additional_facilities list - stores Facility ID only
  const toggleAdditionalFacility = (facilityId) => {
    const current = formData.additional_facilities || "";
    const facilities = current.split(",").map(f => f.trim()).filter(f => f);
    
    if (facilities.includes(facilityId)) {
      // Remove it
      const updated = facilities.filter(f => f !== facilityId).join(", ");
      handleInputChange("additional_facilities", updated);
    } else {
      // Add it
      const updated = [...facilities, facilityId].join(", ");
      handleInputChange("additional_facilities", updated);
    }
  };

  // ==================== STATUS CHANGES HANDLERS ====================

  const handleEntraSearch = async () => {
    if (!entraSearchQuery.trim()) { toast.error("Please enter an Employee ID or email"); return; }
    setEntraLoading(true);
    setEntraUser(null);
    setEntraEditMode(false);
    try {
      const user = await entraApi.searchUser(entraSearchQuery.trim());
      setEntraUser(user);
      setEntraEditData({
        accountEnabled: user.accountEnabled ?? true, employeeType: user.employeeType || "",
        jobTitle: user.jobTitle || "", officeLocation: user.officeLocation || "",
        businessPhones: user.businessPhones || [], mobilePhone: user.mobilePhone || "",
        personalEmail: user.personalEmail || "", facilityName: user.facilityName || "",
        managerEmail: user.managerEmail || "", startDate: user.startDate || "",
        state: user.state || "", companyName: "",
        effectiveDate: new Date().toISOString().split("T")[0],
        comment: ""
      });
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Search failed";
      toast.error(detail);
    } finally { setEntraLoading(false); }
  };

  const handleEntraUpdate = async () => {
    if (!entraUser) return;
    const updates = {};
    if (entraEditData.accountEnabled !== entraUser.accountEnabled) {
      updates.accountEnabled = entraEditData.accountEnabled;
      updates.previousAccountEnabled = entraUser.accountEnabled;
    }
    if (entraEditData.employeeType !== (entraUser.employeeType || "")) {
      updates.employeeType = entraEditData.employeeType || null;
      updates.previousEmployeeType = entraUser.employeeType || null;
    }
    updates.displayName = entraUser.displayName || null;
    if (entraEditData.jobTitle !== (entraUser.jobTitle || "")) updates.jobTitle = entraEditData.jobTitle || null;
    if (entraEditData.officeLocation !== (entraUser.officeLocation || "")) updates.officeLocation = entraEditData.officeLocation || null;
    const newPhone = entraEditData.businessPhones?.[0] || "";
    const oldPhone = entraUser.businessPhones?.[0] || "";
    if (newPhone !== oldPhone) updates.businessPhones = newPhone ? [newPhone] : [];
    if (entraEditData.mobilePhone !== (entraUser.mobilePhone || "")) updates.mobilePhone = entraEditData.mobilePhone || null;
    if (entraEditData.personalEmail !== (entraUser.personalEmail || "")) updates.personalEmail = entraEditData.personalEmail || null;
    if (entraEditData.state !== (entraUser.state || "")) updates.state = entraEditData.state || null;
    if (entraEditData.facilityName !== (entraUser.facilityName || "")) updates.facilityName = entraEditData.facilityName || null;
    if (entraEditData.managerEmail !== (entraUser.managerEmail || "")) updates.managerEmail = entraEditData.managerEmail || null;
    if (entraEditData.startDate !== (entraUser.startDate || "")) updates.startDate = entraEditData.startDate || null;
    // Always include the effective date of change
    if (entraEditData.effectiveDate) updates.effectiveDate = entraEditData.effectiveDate;
    // Always include company code
    updates.companyName = entraEditData.companyName || null;
    // Optional free-form comment from the agent (shown in notification email if non-empty)
    if (entraEditData.comment?.trim()) updates.comment = entraEditData.comment.trim();
    if (Object.keys(updates).filter(k => k !== "displayName" && k !== "previousAccountEnabled" && k !== "previousEmployeeType" && k !== "effectiveDate" && k !== "companyName" && k !== "comment").length === 0) {
      toast.info("No changes to save"); return;
    }
    // Company code is required
    if (!entraEditData.companyName?.trim()) {
      toast.error("Company Code is required"); return;
    }
    // Auto-include any typed-but-not-yet-added emails from the input field
    const allAdditionalEmails = [...entraAdditionalEmails];
    if (entraNewAdditionalEmail.trim()) {
      const pendingEmails = entraNewAdditionalEmail.split(",").map(e => e.trim()).filter(e => e && e.includes("@"));
      pendingEmails.forEach(e => { if (!allAdditionalEmails.includes(e)) allAdditionalEmails.push(e); });
    }
    if (allAdditionalEmails.length > 0) updates.additionalNotifyEmails = allAdditionalEmails;
    if (updates.accountEnabled === false && entraUser.accountEnabled === true) {
      if (!window.confirm(`Are you sure you want to DISABLE this account? This will trigger a termination notification.`)) return;
    }
    if (updates.employeeType && entraUser.employeeType === "FT" && updates.employeeType !== "FT") {
      if (!window.confirm(`Changing from FT will remove M365 licenses. Continue?`)) return;
    }
    setEntraLoading(true);
    try {
      const result = await entraApi.updateUser(entraUser.id, updates);
      toast.success(result.message || "User updated");
      if (result.email_notification) {
        if (result.email_notification.sent) {
          toast.success("Notification email sent");
        } else if (result.email_notification.error) {
          toast.error("Email failed: " + result.email_notification.error);
        }
      }
      const updatedUser = await entraApi.searchUser(entraSearchQuery.trim());
      setEntraUser(updatedUser);
      setEntraEditData({
        accountEnabled: updatedUser.accountEnabled ?? true, employeeType: updatedUser.employeeType || "",
        jobTitle: updatedUser.jobTitle || "", officeLocation: updatedUser.officeLocation || "",
        businessPhones: updatedUser.businessPhones || [], mobilePhone: updatedUser.mobilePhone || "",
        personalEmail: updatedUser.personalEmail || "", facilityName: updatedUser.facilityName || "",
        managerEmail: updatedUser.managerEmail || "", startDate: updatedUser.startDate || ""
      });
      setEntraEditMode(false);
      setEntraAdditionalEmails([]);
      setEntraNewAdditionalEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setEntraLoading(false); }
  };

  const handleEntraCancelEdit = () => {
    if (entraUser) {
      setEntraEditData({
        accountEnabled: entraUser.accountEnabled ?? true, employeeType: entraUser.employeeType || "",
        jobTitle: entraUser.jobTitle || "", officeLocation: entraUser.officeLocation || "",
        businessPhones: entraUser.businessPhones || [], mobilePhone: entraUser.mobilePhone || "",
        personalEmail: entraUser.personalEmail || "", facilityName: entraUser.facilityName || "",
        managerEmail: entraUser.managerEmail || "", startDate: entraUser.startDate || ""
      });
    }
    setEntraEditMode(false);
    setEntraAdditionalEmails([]);
    setEntraNewAdditionalEmail("");
  };

  // Helpers for Status Changes dropdowns - matches AdminPage logic
  const getJobTitles = () => {
    const jt = lists["job_titles"] || [];
    return jt.map(item => {
      if (typeof item === "string") {
        if (item.includes("|")) {
          const parts = item.split("|");
          return { code: parts[0], description: parts[1], full: item };
        }
        return { code: item, description: item, full: item };
      }
      return { code: item.name || item.value || "", description: item.name || item.value || "", full: item.name || item.value || "" };
    });
  };

  const getStatusFacilities = () => {
    const fac = lists["facility_list"] || [];
    return fac.map(item => {
      if (typeof item === "string") {
        if (item.includes("|")) {
          const parts = item.split("|");
          return { id: parts[0], name: parts[1], full: item };
        }
        return { id: item, name: item, full: item };
      }
      return { id: item.name || item.value || "", name: item.name || item.value || "", full: item.name || item.value || "" };
    });
  };

  const getFieldClass = (field) => {
    const hasError = errors[field];
    const isTouched = touched[field];
    const isEmpty = !formData[field];
    const isRequired = requiredFields.includes(field);

    // Show error if has error and touched, show warning only if required and empty
    if (hasError && isTouched) return "field-error";
    if (isRequired && isEmpty && isTouched) return "field-warning";
    return "";
  };

  const renderField = (field, label, type = "text", options = null, size = "normal") => {
    const isRequired = requiredFields.includes(field);
    const value = formData[field] || "";
    const error = errors[field];
    const fieldClass = getFieldClass(field);
    const sizeClass = size === "small" ? "field-small" : "";

    // Searchable dropdown for Manager Email - pulls all company users from Entra ID
    if (field === "manager_email") {
      const filteredManagers = allUsers.filter(m => {
        if (!managerSearch) return true;
        const q = managerSearch.toLowerCase();
        return m.displayName.toLowerCase().includes(q) || m.email.toLowerCase().includes(q);
      });
      return (
        <div className={`compact-field ${sizeClass}`} key={field} ref={managerPickerRef} style={{ position: "relative" }}>
          <label className={`compact-label ${isRequired ? "required" : ""}`}>{label}</label>
          <div
            className={`compact-input ${fieldClass}`}
            onClick={() => selectedEmployee && setShowManagerPicker(!showManagerPicker)}
            style={{ cursor: selectedEmployee ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "space-between" }}
            data-testid={`field-${field}`}
          >
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {value || <span style={{ color: "var(--slate-400)" }}>Select or type...</span>}
            </span>
            <span style={{ marginLeft: "8px" }}>&#9660;</span>
          </div>
          {showManagerPicker && selectedEmployee && (
            <div style={{
              position: "absolute", top: "100%", left: 0, right: 0, background: "white",
              border: "1px solid var(--slate-300)", borderRadius: "5px", maxHeight: "250px",
              overflowY: "auto", zIndex: 100, boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
            }}>
              <div style={{ padding: "0.5rem", borderBottom: "1px solid var(--slate-200)", position: "sticky", top: 0, background: "white" }}>
                <input
                  ref={managerSearchRef}
                  type="text"
                  placeholder="Search by name or email..."
                  value={managerSearch}
                  onChange={(e) => setManagerSearch(e.target.value)}
                  style={{ width: "100%", padding: "0.4rem 0.6rem", border: "1px solid var(--slate-300)", borderRadius: "4px", fontSize: "0.9rem" }}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && managerSearch.trim()) {
                      setFormData(prev => ({ ...prev, manager_email: managerSearch.trim() }));
                      setTouched(prev => ({ ...prev, manager_email: true }));
                      setErrors(prev => { const n = { ...prev }; delete n.manager_email; return n; });
                      setShowManagerPicker(false);
                      setManagerSearch("");
                    }
                  }}
                />
              </div>
              <div
                onClick={() => {
                  setFormData(prev => ({ ...prev, manager_email: "" }));
                  setTouched(prev => ({ ...prev, manager_email: true }));
                  setShowManagerPicker(false);
                  setManagerSearch("");
                }}
                style={{ padding: "0.5rem 0.75rem", cursor: "pointer", borderBottom: "1px solid var(--slate-100)", color: "var(--slate-400)" }}
              >
                Clear selection
              </div>
              {managerSearch.trim() && !filteredManagers.some(m => m.email.toLowerCase() === managerSearch.trim().toLowerCase()) && (
                <div
                  onClick={() => {
                    setFormData(prev => ({ ...prev, manager_email: managerSearch.trim() }));
                    setTouched(prev => ({ ...prev, manager_email: true }));
                    setErrors(prev => { const n = { ...prev }; delete n.manager_email; return n; });
                    setShowManagerPicker(false);
                    setManagerSearch("");
                  }}
                  style={{ padding: "0.5rem 0.75rem", cursor: "pointer", borderBottom: "1px solid var(--slate-200)", background: "var(--primary-50)", fontStyle: "italic" }}
                >
                  Use custom: "{managerSearch.trim()}"
                </div>
              )}
              {filteredManagers.length === 0 && !managerSearch.trim() ? (
                <div style={{ padding: "0.5rem 0.75rem", color: "var(--slate-400)", fontStyle: "italic" }}>
                  {allUsersLoading ? "Loading users..." : allUsersError ? `Error: ${allUsersError}` : "No users found"}
                </div>
              ) : (
                filteredManagers.slice(0, 50).map((member, idx) => (
                  <div
                    key={idx}
                    onClick={() => {
                      setFormData(prev => ({ ...prev, manager_email: member.email }));
                      setTouched(prev => ({ ...prev, manager_email: true }));
                      setErrors(prev => { const n = { ...prev }; delete n.manager_email; return n; });
                      setShowManagerPicker(false);
                      setManagerSearch("");
                    }}
                    style={{
                      padding: "0.5rem 0.75rem", cursor: "pointer", borderBottom: "1px solid var(--slate-100)",
                      background: value === member.email ? "var(--primary-50)" : "transparent"
                    }}
                    onMouseEnter={(e) => e.target.style.background = "var(--slate-50)"}
                    onMouseLeave={(e) => e.target.style.background = value === member.email ? "var(--primary-50)" : "transparent"}
                  >
                    <div style={{ fontWeight: 500 }}>{member.displayName}</div>
                    <div style={{ fontSize: "0.8rem", color: "var(--slate-500)" }}>{member.email}</div>
                  </div>
                ))
              )}
              {filteredManagers.length > 50 && (
                <div style={{ padding: "0.5rem 0.75rem", color: "var(--slate-400)", fontSize: "0.85rem", textAlign: "center" }}>
                  Type to narrow down {filteredManagers.length} results...
                </div>
              )}
            </div>
          )}
          {error && <span className="compact-error">{error}</span>}
        </div>
      );
    }

    // Searchable dropdown for DOR Email - pulls from Entra ID group with custom entry
    if (field === "director_of_rehabilitation_email") {
      const filteredDor = dorMembers.filter(m => {
        if (!dorSearch) return true;
        const q = dorSearch.toLowerCase();
        return m.displayName.toLowerCase().includes(q) || m.email.toLowerCase().includes(q);
      });
      return (
        <div className={`compact-field ${sizeClass}`} key={field} ref={dorPickerRef} style={{ position: "relative" }}>
          <label className={`compact-label ${isRequired ? "required" : ""}`}>{label}</label>
          <div
            className={`compact-input ${fieldClass}`}
            onClick={() => selectedEmployee && setShowDorPicker(!showDorPicker)}
            style={{ cursor: selectedEmployee ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "space-between" }}
            data-testid={`field-${field}`}
          >
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {value || <span style={{ color: "var(--slate-400)" }}>Select or type...</span>}
            </span>
            <span style={{ marginLeft: "8px" }}>&#9660;</span>
          </div>
          {showDorPicker && selectedEmployee && (
            <div style={{
              position: "absolute", top: "100%", left: 0, right: 0, background: "white",
              border: "1px solid var(--slate-300)", borderRadius: "5px", maxHeight: "250px",
              overflowY: "auto", zIndex: 100, boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
            }}>
              <div style={{ padding: "0.5rem", borderBottom: "1px solid var(--slate-200)", position: "sticky", top: 0, background: "white" }}>
                <input
                  ref={dorSearchRef}
                  type="text"
                  placeholder="Search or type custom email..."
                  value={dorSearch}
                  onChange={(e) => setDorSearch(e.target.value)}
                  style={{ width: "100%", padding: "0.4rem 0.6rem", border: "1px solid var(--slate-300)", borderRadius: "4px", fontSize: "0.9rem" }}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && dorSearch.trim()) {
                      setFormData(prev => ({ ...prev, director_of_rehabilitation_email: dorSearch.trim() }));
                      setTouched(prev => ({ ...prev, director_of_rehabilitation_email: true }));
                      setErrors(prev => { const n = { ...prev }; delete n.director_of_rehabilitation_email; return n; });
                      setShowDorPicker(false);
                      setDorSearch("");
                    }
                  }}
                />
              </div>
              <div
                onClick={() => {
                  setFormData(prev => ({ ...prev, director_of_rehabilitation_email: "" }));
                  setTouched(prev => ({ ...prev, director_of_rehabilitation_email: true }));
                  setShowDorPicker(false);
                  setDorSearch("");
                }}
                style={{ padding: "0.5rem 0.75rem", cursor: "pointer", borderBottom: "1px solid var(--slate-100)", color: "var(--slate-400)" }}
              >
                Clear selection
              </div>
              {dorSearch.trim() && !filteredDor.some(m => m.email.toLowerCase() === dorSearch.trim().toLowerCase()) && (
                <div
                  onClick={() => {
                    setFormData(prev => ({ ...prev, director_of_rehabilitation_email: dorSearch.trim() }));
                    setTouched(prev => ({ ...prev, director_of_rehabilitation_email: true }));
                    setErrors(prev => { const n = { ...prev }; delete n.director_of_rehabilitation_email; return n; });
                    setShowDorPicker(false);
                    setDorSearch("");
                  }}
                  style={{ padding: "0.5rem 0.75rem", cursor: "pointer", borderBottom: "1px solid var(--slate-200)", background: "var(--primary-50)", fontStyle: "italic" }}
                >
                  Use custom: "{dorSearch.trim()}"
                </div>
              )}
              {filteredDor.length === 0 && !dorSearch.trim() ? (
                <div style={{ padding: "0.5rem 0.75rem", color: "var(--slate-400)", fontStyle: "italic" }}>
                  {dorLoading ? "Loading group members..." : dorError ? `Error: ${dorError}` : "No members found"}
                </div>
              ) : (
                filteredDor.map((member, idx) => (
                  <div
                    key={idx}
                    onClick={() => {
                      setFormData(prev => ({ ...prev, director_of_rehabilitation_email: member.email }));
                      setTouched(prev => ({ ...prev, director_of_rehabilitation_email: true }));
                      setErrors(prev => { const n = { ...prev }; delete n.director_of_rehabilitation_email; return n; });
                      setShowDorPicker(false);
                      setDorSearch("");
                    }}
                    style={{
                      padding: "0.5rem 0.75rem", cursor: "pointer", borderBottom: "1px solid var(--slate-100)",
                      background: value === member.email ? "var(--primary-50)" : "transparent"
                    }}
                    onMouseEnter={(e) => e.target.style.background = "var(--slate-50)"}
                    onMouseLeave={(e) => e.target.style.background = value === member.email ? "var(--primary-50)" : "transparent"}
                  >
                    <div style={{ fontWeight: 500 }}>{member.displayName}</div>
                    <div style={{ fontSize: "0.8rem", color: "var(--slate-500)" }}>{member.email}</div>
                  </div>
                ))
              )}
            </div>
          )}
          {error && <span className="compact-error">{error}</span>}
        </div>
      );
    }

    // Custom dropdown for home_facility - shows "ID - Name" in dropdown, stores and displays only Name
    if (type === "select" && field === "home_facility" && options) {
      const filteredOptions = options.filter(opt => {
        if (!homeFacilitySearch) return true;
        return opt.toLowerCase().includes(homeFacilitySearch.toLowerCase());
      });
      return (
        <div className={`compact-field ${sizeClass}`} key={field} ref={homeFacilityPickerRef} style={{ position: "relative" }}>
          <label className={`compact-label ${isRequired ? "required" : ""}`}>{label}</label>
          <div
            className={`compact-input ${fieldClass}`}
            onClick={() => selectedEmployee && setShowHomeFacilityPicker(!showHomeFacilityPicker)}
            style={{ cursor: selectedEmployee ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "space-between" }}
            data-testid={`field-${field}`}
          >
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {value || <span style={{ color: "var(--slate-400)" }}>Select...</span>}
            </span>
            <span style={{ marginLeft: "8px" }}>▼</span>
          </div>
          {showHomeFacilityPicker && selectedEmployee && (
            <div style={{
              position: "absolute", top: "100%", left: 0, right: 0, background: "white",
              border: "1px solid var(--slate-300)", borderRadius: "5px", maxHeight: "250px",
              overflowY: "auto", zIndex: 100, boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
            }}>
              <div style={{ padding: "0.5rem", borderBottom: "1px solid var(--slate-200)", position: "sticky", top: 0, background: "white" }}>
                <input
                  ref={homeFacilitySearchRef}
                  type="text"
                  placeholder="Search by JBS, Ignite, or facility name..."
                  value={homeFacilitySearch}
                  onChange={(e) => setHomeFacilitySearch(e.target.value)}
                  style={{ width: "100%", padding: "0.4rem 0.6rem", border: "1px solid var(--slate-300)", borderRadius: "4px", fontSize: "0.9rem" }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div
                onClick={() => { 
                  setFormData(prev => ({ ...prev, home_facility: "", facility_id: "" }));
                  setTouched(prev => ({ ...prev, home_facility: true }));
                  // Set error since field is now empty and it's required
                  setErrors(prev => ({ ...prev, home_facility: "Required" }));
                  setShowHomeFacilityPicker(false); 
                  setHomeFacilitySearch(""); 
                }}
                style={{ padding: "0.5rem 0.75rem", cursor: "pointer", borderBottom: "1px solid var(--slate-100)", color: "var(--slate-400)" }}
              >
                Clear selection
              </div>
              {filteredOptions.length === 0 ? (
                <div style={{ padding: "0.5rem 0.75rem", color: "var(--slate-400)", fontStyle: "italic" }}>No matches found</div>
              ) : (
                filteredOptions.map((opt) => {
                  const parts = opt.split("|");
                  const facilityId = parts[0];
                  const nameOnly = parts.length > 1 ? parts[1] : parts[0];
                  const igniteId = parts.length > 2 ? parts[2] : "";
                  const displayText = parts.length > 1 ? `${parts[0]} - ${parts[1]}` : parts[0];
                  return (
                    <div
                      key={opt}
                      onClick={() => { 
                        // Set both home_facility (name) and facility_id
                        setFormData(prev => ({ ...prev, home_facility: nameOnly, facility_id: facilityId }));
                        setTouched(prev => ({ ...prev, home_facility: true, facility_id: true }));
                        // Clear any validation errors for home_facility since we just selected a value
                        setErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.home_facility;
                          return newErrors;
                        });
                        setShowHomeFacilityPicker(false); 
                        setHomeFacilitySearch("");
                        // Auto-lookup facility email from Entra ID by facility code
                        if (facilityId) {
                          entraApi.getFacilityContact(facilityId).then(result => {
                            if (result.found && result.email) {
                              setFormData(prev => ({ ...prev, facility_email: result.email }));
                              setTouched(prev => ({ ...prev, facility_email: true }));
                            }
                          }).catch(err => {
                            console.error("Facility contact lookup failed:", err);
                          });
                        }
                      }}
                      style={{
                        padding: "0.5rem 0.75rem", cursor: "pointer",
                        background: value === nameOnly ? "var(--primary-100)" : "transparent",
                        borderBottom: "1px solid var(--slate-100)"
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
                        <span>{displayText}</span>
                        {igniteId && (
                          <span style={{ fontSize: "0.78rem", color: "var(--slate-500)" }} title="Ignite ID">
                            Ignite: {igniteId}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
          {error && touched[field] && <span className="field-error-text">{error}</span>}
        </div>
      );
    }

    // Custom dropdown for job_title - shows "Code - Description" in dropdown, stores and displays only Description
    if (type === "select" && field === "job_title" && options) {
      const filteredOptions = options.filter(opt => {
        if (!jobTitleSearch) return true;
        return opt.toLowerCase().includes(jobTitleSearch.toLowerCase());
      });
      return (
        <div className={`compact-field ${sizeClass}`} key={field} ref={jobTitlePickerRef} style={{ position: "relative" }}>
          <label className={`compact-label ${isRequired ? "required" : ""}`}>{label}</label>
          <div
            className={`compact-input ${fieldClass}`}
            onClick={() => selectedEmployee && setShowJobTitlePicker(!showJobTitlePicker)}
            style={{ cursor: selectedEmployee ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "space-between" }}
            data-testid={`field-${field}`}
          >
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {value || <span style={{ color: "var(--slate-400)" }}>Select...</span>}
            </span>
            <span style={{ marginLeft: "8px" }}>▼</span>
          </div>
          {showJobTitlePicker && selectedEmployee && (
            <div style={{
              position: "absolute", top: "100%", left: 0, right: 0, background: "white",
              border: "1px solid var(--slate-300)", borderRadius: "5px", maxHeight: "250px",
              overflowY: "auto", zIndex: 100, boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
            }}>
              <div style={{ padding: "0.5rem", borderBottom: "1px solid var(--slate-200)", position: "sticky", top: 0, background: "white" }}>
                <input
                  ref={jobTitleSearchRef}
                  type="text"
                  placeholder="Type to search..."
                  value={jobTitleSearch}
                  onChange={(e) => setJobTitleSearch(e.target.value)}
                  style={{ width: "100%", padding: "0.4rem 0.6rem", border: "1px solid var(--slate-300)", borderRadius: "4px", fontSize: "0.9rem" }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div
                onClick={() => { handleInputChange(field, ""); setShowJobTitlePicker(false); setJobTitleSearch(""); }}
                style={{ padding: "0.5rem 0.75rem", cursor: "pointer", borderBottom: "1px solid var(--slate-100)", color: "var(--slate-400)" }}
              >
                Clear selection
              </div>
              {filteredOptions.length === 0 ? (
                <div style={{ padding: "0.5rem 0.75rem", color: "var(--slate-400)", fontStyle: "italic" }}>No matches found</div>
              ) : (
                filteredOptions.map((opt) => {
                  const parts = opt.split("|");
                  const descOnly = parts.length > 1 ? parts[1] : parts[0];
                  const displayText = parts.length > 1 ? `${parts[0]} - ${parts[1]}` : parts[0];
                  return (
                    <div
                      key={opt}
                      onClick={() => { handleInputChange(field, descOnly); setShowJobTitlePicker(false); setJobTitleSearch(""); }}
                      style={{
                        padding: "0.5rem 0.75rem", cursor: "pointer",
                        background: value === descOnly ? "var(--primary-100)" : "transparent",
                        borderBottom: "1px solid var(--slate-100)"
                      }}
                    >
                      {displayText}
                    </div>
                  );
                })
              )}
            </div>
          )}
          {error && touched[field] && <span className="field-error-text">{error}</span>}
        </div>
      );
    }

    if (type === "select" && options) {
      return (
        <div className={`compact-field ${sizeClass}`} key={field}>
          <label className={`compact-label ${isRequired ? "required" : ""}`}>{label}</label>
          <select
            className={`compact-input ${fieldClass}`}
            value={value}
            onChange={(e) => handleInputChange(field, e.target.value)}
            disabled={!selectedEmployee}
            data-testid={`field-${field}`}
          >
            <option value="">Select...</option>
            {options.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          {error && touched[field] && <span className="field-error-text">{error}</span>}
        </div>
      );
    }

    if (type === "date") {
      return (
        <div className={`compact-field ${sizeClass}`} key={field}>
          <label className={`compact-label ${isRequired ? "required" : ""}`}>{label}</label>
          <input
            type="date"
            className={`compact-input ${fieldClass}`}
            value={formatDate(value)}
            onChange={(e) => handleInputChange(field, e.target.value)}
            disabled={!selectedEmployee}
            data-testid={`field-${field}`}
          />
          {error && touched[field] && <span className="field-error-text">{error}</span>}
        </div>
      );
    }

    return (
      <div className={`compact-field ${sizeClass}`} key={field}>
        <label className={`compact-label ${isRequired ? "required" : ""}`}>{label}</label>
        <input
          type={type}
          className={`compact-input ${fieldClass}`}
          value={value}
          onChange={(e) => handleInputChange(field, e.target.value)}
          disabled={!selectedEmployee}
          placeholder={label}
          data-testid={`field-${field}`}
        />
        {error && touched[field] && <span className="field-error-text">{error}</span>}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const missingCount = requiredFields.filter((f) => !formData[f]).length;
  const errorCount = Object.keys(errors).length;

  return (
    <div className="app-layout">
      <header className="app-header">
        <div className="header-left">
          <div className="header-logo">
            <img src="/tmc-onboarding-logo.png" alt="TMC Onboarding" style={{ height: "50px", width: "auto" }} />
          </div>
        </div>

        <nav className="header-nav">
          <Link to="/" className="nav-link active">Process Employees</Link>
          {isAdmin && <Link to="/admin" className="nav-link">Admin Panel</Link>}
        </nav>

        <div className="header-right">
          <WhatsNewBell />
          <button
            data-testid="help-button"
            onClick={() => setShowHelp(true)}
            title="Help & FAQ"
            style={{
              width: "36px", height: "36px", borderRadius: "50%",
              border: "2px solid var(--primary-200, #bfdbfe)", background: "var(--primary-50, #eff6ff)",
              color: "var(--primary-600, #2563eb)", cursor: "pointer", display: "flex",
              alignItems: "center", justifyContent: "center",
              fontSize: "1rem", fontWeight: 700,
              transition: "all 0.15s", marginRight: "0.25rem"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--primary-100, #dbeafe)";
              e.currentTarget.style.borderColor = "var(--primary-400, #60a5fa)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--primary-50, #eff6ff)";
              e.currentTarget.style.borderColor = "var(--primary-200, #bfdbfe)";
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-600, #2563eb)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </button>
          <div className="user-info">
            <div className="user-avatar">{user?.name?.charAt(0) || user?.email?.charAt(0) || "U"}</div>
            <div>
              <div className="user-name">{user?.name || user?.email}</div>
              <div className="user-role">{user?.role}</div>
            </div>
          </div>
          <button className="logout-btn" onClick={logout} data-testid="logout-btn">Sign Out</button>
        </div>
      </header>

      <main className="main-content compact">
        {/* Tab Navigation */}
        <div style={{ display: "flex", gap: "0", marginBottom: "1rem", borderBottom: "2px solid var(--slate-200)" }}>
          <button
            onClick={() => setMainTab("process")}
            data-testid="tab-process"
            style={{
              padding: "0.65rem 1.5rem", border: "none", cursor: "pointer", fontWeight: 600, fontSize: "0.95rem",
              background: mainTab === "process" ? "var(--primary-600)" : "transparent",
              color: mainTab === "process" ? "white" : "var(--slate-600)",
              borderRadius: "6px 6px 0 0",
              borderBottom: mainTab === "process" ? "2px solid var(--primary-600)" : "2px solid transparent",
              marginBottom: "-2px"
            }}
          >
            Process Employees
          </button>
          <button
            onClick={() => setMainTab("statusChanges")}
            data-testid="tab-status-changes"
            style={{
              padding: "0.65rem 1.5rem", border: "none", cursor: "pointer", fontWeight: 600, fontSize: "0.95rem",
              background: mainTab === "statusChanges" ? "var(--primary-600)" : "transparent",
              color: mainTab === "statusChanges" ? "white" : "var(--slate-600)",
              borderRadius: "6px 6px 0 0",
              borderBottom: mainTab === "statusChanges" ? "2px solid var(--primary-600)" : "2px solid transparent",
              marginBottom: "-2px"
            }}
          >
            Status Changes
          </button>
          <button
            onClick={() => setMainTab("processed")}
            data-testid="tab-processed-employees"
            style={{
              padding: "0.65rem 1.5rem", border: "none", cursor: "pointer", fontWeight: 600, fontSize: "0.95rem",
              background: mainTab === "processed" ? "var(--primary-600)" : "transparent",
              color: mainTab === "processed" ? "white" : "var(--slate-600)",
              borderRadius: "6px 6px 0 0",
              borderBottom: mainTab === "processed" ? "2px solid var(--primary-600)" : "2px solid transparent",
              marginBottom: "-2px"
            }}
          >
            Processed Employees
          </button>
        </div>

        {mainTab === "process" && (<>
        {/* Stats Bar */}
        <div className="stats-bar">
          <div className="stat-item"><span className="stat-num">{myStats?.total || 0}</span> Submitted</div>
          <div className="stat-item"><span className="stat-num">{myStats?.successful || 0}</span> Successful</div>
          <div className="stat-item"><span className="stat-num">{employees.length}</span> Pending</div>
          <div className="stat-item"><span className="stat-num">{myStats?.success_rate || 0}%</span> Rate</div>
        </div>

        {/* Employee Selector */}
        <div className="selector-bar">
          <label>Select Employee:</label>
          <select
            className="selector-dropdown"
            value={selectedEmployee?.isNew ? "" : (selectedEmployee?.id || "")}
            onChange={handleEmployeeSelect}
            data-testid="employee-selector"
            disabled={selectedEmployee?.isNew}
          >
            <option value="">-- Select Employee to Process --</option>
            {employees.map((emp) => {
              const lock = getEmployeeLock(emp.id);
              const isLocked = !!lock;
              const isPending = emp.submission_status === "pending";
              const isError = emp.submission_status === "error";
              const isDisabled = isLocked || isPending;
              return (
                <option 
                  key={emp.id} 
                  value={emp.id}
                  disabled={isDisabled}
                  style={isDisabled ? { color: '#999', fontStyle: 'italic' } : isError ? { color: '#dc2626' } : {}}
                >
                  {isLocked ? '🔒 ' : ''}{isPending ? '⏳ ' : ''}{isError ? '❌ ' : ''}{emp.last_name}, {emp.first_name} - {emp.position_id || "No Position ID"}
                  {isLocked ? ` (editing: ${lock.locked_by})` : ''}
                  {isPending ? ' (pending submission)' : ''}
                  {isError ? ' (FAILED - needs retry)' : ''}
                </option>
              );
            })}
          </select>
          <button
            className="btn btn-success"
            onClick={handleAddNewEmployee}
            style={{ marginLeft: "1rem" }}
            data-testid="add-new-employee-btn"
          >
            + Add New Employee
          </button>
          {selectedEmployee && (
            <>
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleReleaseLock}
                style={{ marginLeft: "1rem" }}
                title="Release lock and close this record"
              >
                Release & Close
              </button>
              <div className="validation-summary">
                {missingCount > 0 && <span className="summary-warning">{missingCount} required fields missing</span>}
                {errorCount > 0 && missingCount === 0 && <span className="summary-error">{errorCount} fields have errors</span>}
                {errorCount === 0 && missingCount === 0 && <span className="summary-success">Ready to submit</span>}
              </div>
            </>
          )}
        </div>

        {/* Lock Error Popup */}
        {lockError && (
          <div className="modal-overlay" onClick={() => setLockError(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "400px" }}>
              <div className="modal-header">
                <h3 className="modal-title">🔒 Record Locked</h3>
                <button className="modal-close" onClick={() => setLockError(null)}>×</button>
              </div>
              <div className="modal-body">
                <p style={{ textAlign: "center", fontSize: "1.1rem" }}>{lockError}</p>
                <p style={{ textAlign: "center", color: "var(--slate-500)", marginTop: "0.5rem" }}>
                  Please try again later or contact them to release the record.
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={() => setLockError(null)}>OK</button>
              </div>
            </div>
          </div>
        )}

        {/* Compact Form */}
        {selectedEmployee && (
          <div className="compact-form-container">
            {/* New Employee Banner */}
            {selectedEmployee.isNew && (
              <div style={{
                background: "linear-gradient(135deg, var(--primary-500), var(--primary-600))",
                color: "white",
                padding: "0.75rem 1rem",
                borderRadius: "8px",
                marginBottom: "1rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem"
              }}>
                <span style={{ fontSize: "1.25rem" }}>➕</span>
                <span style={{ fontWeight: 600 }}>Adding New Employee</span>
                <span style={{ opacity: 0.8, marginLeft: "auto" }}>Fill in all required fields to create a new employee record</span>
              </div>
            )}
            
            {/* Row 1: Personal Info */}
            <div className="form-row">
              <div className="form-section-header">Personal Information</div>
            </div>
            <div className="form-row six-col">
              {renderField("position_id", "Position ID", "text", null, "small")}
              {renderField("first_name", "First Name", "text", null, "small")}
              {renderField("middle_name", "Middle Name", "text", null, "small")}
              {renderField("last_name", "Last Name", "text", null, "small")}
              {renderField("birth_date", "Birth Date", "date", null, "small")}
              {renderField("worked_state_tax_code", "State Tax Code", "text", null, "small")}
            </div>

            {/* Row 2: Contact */}
            <div className="form-row four-col">
              {renderField("personal_email", "Personal Email", "email")}
              {renderField("home_phone_number", "Mobile Phone", "tel")}
              {renderField("manager_email", "Manager Email", "email")}
              {renderField("director_of_rehabilitation_email", "DOR Email", "email")}
            </div>

            {/* Row 3: Employment */}
            <div className="form-row">
              <div className="form-section-header">Employment Information</div>
            </div>
            <div className="form-row six-col">
              {renderField("position_start_date", "Start Date", "date", null, "small")}
              {renderField("pay_group", "Pay Group", "text", null, "small")}
              {renderField("employee_status", "Status", "select", lists.employee_status || ["Active", "Pending", "Inactive"], "small")}
              {renderField("employee_type", "Type", "select", ["FT", "PT", "PRN", "Contractor"], "small")}
              {renderField("job_title", "Job Title", "select", lists.job_titles || [], "small")}
              {renderField("home_facility", "Home Facility", "select", lists.facility_list || [], "small")}
              {/* Facility ID - Auto-populated from Home Facility selection */}
              <div className="compact-field field-small">
                <label className="compact-label">Facility ID</label>
                <input
                  type="text"
                  className="compact-input"
                  value={formData.facility_id || ""}
                  readOnly
                  disabled
                  placeholder="Auto-filled"
                  data-testid="field-facility_id"
                  style={{ backgroundColor: "var(--slate-100)", cursor: "not-allowed" }}
                />
              </div>
            </div>

            {/* Row 4: More Employment */}
            <div className="form-row four-col">
              {renderField("roles", "Roles", "select", lists.roles || [])}
              {/* Additional Facilities - Multi-select from facility list, stores Facility IDs */}
              <div className="compact-field">
                <label className="compact-label">Additional Facilities</label>
                <div style={{ position: "relative" }} ref={facilityPickerRef}>
                  <div style={{ display: "flex", gap: "0.25rem" }}>
                    <input
                      type="text"
                      className="compact-input"
                      value={formData.additional_facilities || ""}
                      onClick={() => setShowFacilityPicker(!showFacilityPicker)}
                      readOnly
                      placeholder="Click to select facilities..."
                      disabled={!selectedEmployee}
                      data-testid="field-additional_facilities"
                      style={{ flex: 1 }}
                    />
                    {formData.additional_facilities && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData(prev => ({ ...prev, additional_facilities: "" }));
                        }}
                        style={{
                          background: "var(--slate-200)",
                          border: "none",
                          borderRadius: "4px",
                          padding: "0 0.5rem",
                          cursor: "pointer",
                          fontSize: "1rem"
                        }}
                        title="Clear"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {showFacilityPicker && selectedEmployee && (
                    <div style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      background: "white",
                      border: "1px solid var(--slate-300)",
                      borderRadius: "5px",
                      maxHeight: "280px",
                      overflowY: "auto",
                      zIndex: 100,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                    }}>
                      {/* Search input */}
                      <div style={{ padding: "0.5rem", borderBottom: "1px solid var(--slate-200)", position: "sticky", top: 0, background: "white" }}>
                        <input
                          type="text"
                          placeholder="Search by JBS, Ignite, or facility name..."
                          value={additionalFacilitySearch}
                          onChange={(e) => setAdditionalFacilitySearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ width: "100%", padding: "0.4rem 0.6rem", border: "1px solid var(--slate-300)", borderRadius: "4px", fontSize: "0.9rem" }}
                        />
                      </div>
                      {(lists.facility_list || [])
                        .filter(opt => {
                          if (!additionalFacilitySearch) return true;
                          return opt.toLowerCase().includes(additionalFacilitySearch.toLowerCase());
                        })
                        .map((opt) => {
                        const parts = opt.split("|");
                        const facilityId = parts[0]; // Use ID (first part)
                        const igniteId = parts.length > 2 ? parts[2] : "";
                        const displayText = parts.length > 1 ? `${parts[0]} - ${parts[1]}` : parts[0];
                        const currentIds = (formData.additional_facilities || "").split(",").map(f => f.trim()).filter(f => f);
                        const isSelected = currentIds.includes(facilityId);
                        return (
                          <div
                            key={opt}
                            onClick={() => toggleAdditionalFacility(facilityId)}
                            style={{
                              padding: "0.5rem 0.75rem",
                              cursor: "pointer",
                              background: isSelected ? "var(--primary-100)" : "transparent",
                              borderBottom: "1px solid var(--slate-100)",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem"
                            }}
                          >
                            <input type="checkbox" checked={isSelected} readOnly />
                            <span style={{ flex: 1 }}>{displayText}</span>
                            {igniteId && (
                              <span style={{ fontSize: "0.78rem", color: "var(--slate-500)" }} title="Ignite ID">
                                Ignite: {igniteId}
                              </span>
                            )}
                          </div>
                        );
                      })}
                      {(lists.facility_list || []).filter(opt => {
                        if (!additionalFacilitySearch) return true;
                        return opt.toLowerCase().includes(additionalFacilitySearch.toLowerCase());
                      }).length === 0 && (
                        <div style={{ padding: "0.5rem 0.75rem", color: "var(--slate-400)", fontStyle: "italic" }}>No matches found</div>
                      )}
                      <div style={{ display: "flex", borderTop: "1px solid var(--slate-200)", position: "sticky", bottom: 0, background: "white" }}>
                        <div
                          onClick={() => {
                            setFormData(prev => ({ ...prev, additional_facilities: "" }));
                          }}
                          style={{
                            padding: "0.5rem",
                            textAlign: "center",
                            background: "var(--slate-50)",
                            cursor: "pointer",
                            fontWeight: 500,
                            flex: 1,
                            color: "var(--error)"
                          }}
                        >
                          Clear All
                        </div>
                        <div
                          onClick={() => { setShowFacilityPicker(false); setAdditionalFacilitySearch(""); }}
                          style={{
                            padding: "0.5rem",
                            textAlign: "center",
                            background: "var(--primary-100)",
                            cursor: "pointer",
                            fontWeight: 500,
                            flex: 1
                          }}
                        >
                          Done
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {renderField("facility_email", "Facility Email", "email")}
              {renderField("adp_code", "ADP Code", "text")}
            </div>

            {/* Row 5: License */}
            <div className="form-row">
              <div className="form-section-header">License & Classification</div>
            </div>
            <div className="form-row six-col">
              {renderField("license_state", "License State", "text", null, "small")}
              {renderField("license_number", "License #", "text", null, "small")}
              {renderField("license_issue_date", "Issue Date", "date", null, "small")}
              {renderField("license_expiration", "Expiration", "date", null, "small")}
              {renderField("discipline", "Discipline", "select", lists.discipline || [], "small")}
              {renderField("designation", "Designation", "select", lists.designation || [], "small")}
            </div>

            {/* Row 6: Admin */}
            <div className="form-row four-col">
              {renderField("ops_admin", "Ops/Admin", "select", lists.ops_admin || ["Ops", "Admin"])}
              {renderField("clock_in_out", "Clock In/Out", "select", lists.clock_in_out || ["Yes", "No"])}
              {renderField("is_a_tcn", "Is a DOR?", "select", ["Yes", "No"])}
              <div className="compact-field">
                <label className="compact-label">HR Agent</label>
                <input type="text" className="compact-input" value={user?.email || ""} disabled />
              </div>
            </div>

            {/* Notes Section - Internal use only */}
            <div className="form-row" style={{ marginTop: "1rem" }}>
              <div className="compact-field" style={{ width: "100%" }}>
                <label className="compact-label">
                  📝 Internal Notes <span style={{ fontWeight: "normal", color: "var(--slate-500)", fontSize: "0.8rem" }}>(not sent to Power Automate)</span>
                </label>
                <textarea
                  className="compact-input"
                  value={formData.notes || ""}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Add any internal notes about this employee..."
                  disabled={!selectedEmployee}
                  data-testid="field-notes"
                  style={{ 
                    minHeight: "80px", 
                    resize: "vertical",
                    fontFamily: "inherit"
                  }}
                />
              </div>
            </div>

            {/* Error Summary */}
            {Object.keys(errors).length > 0 && (
              <div style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                padding: "1rem",
                marginTop: "1rem"
              }}>
                <div style={{ color: "#dc2626", fontWeight: 600, marginBottom: "0.5rem" }}>
                  ⚠️ {Object.keys(errors).length} field(s) have errors:
                </div>
                <ul style={{ margin: 0, paddingLeft: "1.5rem", color: "#991b1b" }}>
                  {Object.entries(errors).map(([field, error]) => {
                    const fieldLabels = {
                      position_id: "Position ID",
                      position_start_date: "Start Date",
                      pay_group: "Pay Group",
                      employee_status: "Status",
                      first_name: "First Name",
                      middle_name: "Middle Name",
                      last_name: "Last Name",
                      personal_email: "Personal Email",
                      home_phone_number: "Mobile Phone",
                      birth_date: "Birth Date",
                      worked_state_tax_code: "Worked State Tax Code",
                      job_title: "Job Title",
                      employee_type: "Type",
                      home_facility: "Home Facility",
                      roles: "Roles",
                      additional_facilities: "Additional Facilities",
                      facility_email: "Facility Email",
                      adp_code: "ADP Code",
                      license_state: "License State",
                      license_number: "License #",
                      license_issue_date: "License Issue Date",
                      license_expiration: "License Expiration",
                      discipline: "Discipline",
                      designation: "Designation",
                      manager_email: "Manager Email",
                      director_of_rehabilitation_email: "Director of Rehab Email",
                      ops_admin: "Ops/Admin",
                      clock_in_out: "Clock In/Out",
                      is_a_tcn: "Is a DOR?",
                    };
                    const label = fieldLabels[field] || field;
                    return (
                      <li key={field} style={{ fontSize: "0.9rem" }}>
                        <strong>{label}</strong>: {error}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Action Buttons */}
            <div className="submit-bar" style={{ gap: "1rem" }}>
              <button
                className="btn btn-secondary btn-large"
                onClick={handleSave}
                disabled={saving}
                data-testid="save-btn"
              >
                {saving ? "Saving..." : (selectedEmployee?.isNew ? "Create Draft" : "Save Draft")}
              </button>
              <button
                className="btn btn-primary btn-large"
                onClick={handleSubmit}
                disabled={!isFormValid() || submitting}
                data-testid="submit-btn"
              >
                {submitting ? "Submitting..." : (selectedEmployee?.isNew ? "Create & Submit" : "Submit Employee")}
              </button>
            </div>
          </div>
        )}

        {!selectedEmployee && employees.length === 0 && (
          <div className="empty-state-compact">
            <p>No unprocessed employees. All caught up!</p>
          </div>
        )}
        </>)}

        {/* STATUS CHANGES TAB */}
        {mainTab === "statusChanges" && (
          <div className="card" data-testid="status-changes-tab">
            <div className="card-header">
              <h3>Entra ID Status Changes</h3>
            </div>
            <div style={{ padding: "1rem" }}>
              {/* Search */}
              <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem" }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Search by Employee ID or email address..."
                  value={entraSearchQuery}
                  onChange={(e) => setEntraSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleEntraSearch()}
                  style={{ flex: 1 }}
                  data-testid="entra-search-input-main"
                />
                <button className="btn btn-primary" onClick={handleEntraSearch} disabled={entraLoading} data-testid="entra-search-btn-main">
                  {entraLoading ? "Searching..." : "Search"}
                </button>
              </div>

              {/* User Display */}
              {entraUser && (
                <div style={{ border: "1px solid var(--slate-200)", borderRadius: "8px", padding: "1rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                    <div>
                      <h4 style={{ margin: 0 }}>{entraUser.displayName}</h4>
                      <p style={{ margin: "0.2rem 0 0", color: "var(--slate-500)", fontSize: "0.9rem" }}>{entraUser.userPrincipalName}</p>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                      <span style={{
                        padding: "0.25rem 0.75rem", borderRadius: "999px", fontSize: "0.8rem", fontWeight: 600,
                        background: entraUser.accountEnabled ? "#dcfce7" : "#fee2e2",
                        color: entraUser.accountEnabled ? "#166534" : "#991b1b"
                      }}>
                        {entraUser.accountEnabled ? "Enabled" : "Disabled"}
                      </span>
                      {!entraEditMode && (
                        <button className="btn btn-primary" onClick={() => setEntraEditMode(true)} style={{ fontSize: "0.85rem" }} data-testid="entra-edit-btn-main">
                          Edit
                        </button>
                      )}
                    </div>
                  </div>

                  {!entraEditMode ? (
                    /* View Mode */
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem" }}>
                      <div>
                        <label style={{ color: "var(--slate-500)", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Employee ID</label>
                        <p style={{ margin: "0.25rem 0 0", fontWeight: 500 }}>{entraUser.employeeId || "—"}</p>
                      </div>
                      <div>
                        <label style={{ color: "var(--slate-500)", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Employee Type</label>
                        <p style={{ margin: "0.25rem 0 0", fontWeight: 500 }}>{entraUser.employeeType || "—"}</p>
                      </div>
                      <div>
                        <label style={{ color: "var(--slate-500)", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Job Title</label>
                        <p style={{ margin: "0.25rem 0 0", fontWeight: 500 }}>{entraUser.jobTitle || "—"}</p>
                      </div>
                      <div>
                        <label style={{ color: "var(--slate-500)", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Office Location</label>
                        <p style={{ margin: "0.25rem 0 0", fontWeight: 500 }}>{entraUser.officeLocation || "—"}</p>
                      </div>
                      <div>
                        <label style={{ color: "var(--slate-500)", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Business Phone</label>
                        <p style={{ margin: "0.25rem 0 0", fontWeight: 500 }}>{entraUser.businessPhones?.[0] || "—"}</p>
                      </div>
                      <div>
                        <label style={{ color: "var(--slate-500)", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Mobile Phone</label>
                        <p style={{ margin: "0.25rem 0 0", fontWeight: 500 }}>{entraUser.mobilePhone || "—"}</p>
                      </div>
                      <div>
                        <label style={{ color: "var(--slate-500)", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>State</label>
                        <p style={{ margin: "0.25rem 0 0", fontWeight: 500 }}>{entraUser.state || "—"}</p>
                      </div>
                      <div>
                        <label style={{ color: "var(--slate-500)", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Company</label>
                        <p style={{ margin: "0.25rem 0 0", fontWeight: 500 }}>{entraUser.companyName || "—"}</p>
                      </div>
                      <div>
                        <label style={{ color: "var(--slate-500)", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Personal Email (Attr 14)</label>
                        <p style={{ margin: "0.25rem 0 0", fontWeight: 500 }}>{entraUser.personalEmail || "—"}</p>
                      </div>
                      <div>
                        <label style={{ color: "var(--slate-500)", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Facility Name (Attr 02)</label>
                        <p style={{ margin: "0.25rem 0 0", fontWeight: 500 }}>{entraUser.facilityName || "—"}</p>
                      </div>
                      <div>
                        <label style={{ color: "var(--slate-500)", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Manager (Attr 10)</label>
                        <p style={{ margin: "0.25rem 0 0", fontWeight: 500 }}>{entraUser.managerEmail || "—"}</p>
                      </div>
                      <div>
                        <label style={{ color: "var(--slate-500)", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Start Date (Attr 15)</label>
                        <p style={{ margin: "0.25rem 0 0", fontWeight: 500 }}>{entraUser.startDate || "—"}</p>
                      </div>
                      <div>
                        <label style={{ color: "var(--slate-500)", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Department</label>
                        <p style={{ margin: "0.25rem 0 0", fontWeight: 500 }}>{entraUser.department || "—"}</p>
                      </div>
                    </div>
                  ) : (
                    /* Edit Mode */
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Account Status</label>
                        <select className="form-select" value={entraEditData.accountEnabled ? "true" : "false"}
                          onChange={(e) => setEntraEditData({...entraEditData, accountEnabled: e.target.value === "true"})}
                          data-testid="entra-edit-status-main">
                          <option value="true">Enabled</option>
                          <option value="false">Disabled</option>
                        </select>
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Employee Type</label>
                        <select className="form-select" value={entraEditData.employeeType}
                          onChange={(e) => setEntraEditData({...entraEditData, employeeType: e.target.value})}
                          data-testid="entra-edit-type-main">
                          <option value="">-- Select --</option>
                          <option value="FT">FT (Full-Time)</option>
                          <option value="PT">PT (Part-Time)</option>
                          <option value="PRN">PRN</option>
                          <option value="Contractor">Contractor</option>
                        </select>
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Job Title</label>
                        <select className="form-select" value={entraEditData.jobTitle}
                          onChange={(e) => setEntraEditData({...entraEditData, jobTitle: e.target.value})}
                          data-testid="entra-edit-title-main">
                          <option value="">-- Select Job Title --</option>
                          {getJobTitles().map((jt, idx) => <option key={idx} value={jt.description}>{jt.description}</option>)}
                        </select>
                      </div>
                      <div className="form-group" style={{ margin: 0, position: "relative" }} ref={entraFacilityPickerRef}>
                        <label className="form-label">Office Location / Facility</label>
                        <input type="text" className="form-input" 
                          value={showEntraFacilityPicker ? entraFacilitySearch : entraEditData.officeLocation}
                          onChange={(e) => { setEntraFacilitySearch(e.target.value); setShowEntraFacilityPicker(true); }}
                          onFocus={() => { setEntraFacilitySearch(""); setShowEntraFacilityPicker(true); }}
                          placeholder="Search facilities..."
                          data-testid="entra-edit-location-main"
                          autoComplete="off" />
                        {showEntraFacilityPicker && (
                          <div style={{ position: "absolute", zIndex: 100, top: "100%", left: 0, right: 0, maxHeight: "200px", overflowY: "auto", background: "white", border: "1px solid var(--slate-200)", borderRadius: "0 0 6px 6px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
                            {getStatusFacilities().filter(f => !entraFacilitySearch || f.name.toLowerCase().includes(entraFacilitySearch.toLowerCase())).map((f, idx) => (
                              <div key={idx} style={{ padding: "0.5rem 0.75rem", cursor: "pointer", fontSize: "0.88rem", borderBottom: "1px solid var(--slate-100)" }}
                                onMouseEnter={(e) => e.currentTarget.style.background = "var(--primary-50)"}
                                onMouseLeave={(e) => e.currentTarget.style.background = "white"}
                                onClick={() => {
                                  setEntraEditData(prev => ({...prev, officeLocation: f.name, facilityName: f.name}));
                                  setShowEntraFacilityPicker(false);
                                  setEntraFacilitySearch("");
                                }}
                                data-testid={`entra-facility-option-${idx}`}
                              >{f.name}</div>
                            ))}
                            {getStatusFacilities().filter(f => !entraFacilitySearch || f.name.toLowerCase().includes(entraFacilitySearch.toLowerCase())).length === 0 && (
                              <div style={{ padding: "0.5rem 0.75rem", fontSize: "0.85rem", color: "var(--slate-400)" }}>No matching facilities</div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Business Phone</label>
                        <input type="tel" className="form-input" value={entraEditData.businessPhones?.[0] || ""}
                          onChange={(e) => setEntraEditData({...entraEditData, businessPhones: [e.target.value]})}
                          data-testid="entra-edit-phone-main" />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Mobile Phone</label>
                        <input type="tel" className="form-input" value={entraEditData.mobilePhone}
                          onChange={(e) => setEntraEditData({...entraEditData, mobilePhone: e.target.value})}
                          data-testid="entra-edit-mobile-main" />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">State</label>
                        <input type="text" className="form-input" value={entraEditData.state}
                          onChange={(e) => setEntraEditData({...entraEditData, state: e.target.value})}
                          placeholder="e.g., TX, CA, FL"
                          data-testid="entra-edit-state-main" />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Personal Email (Attr 14)</label>
                        <input type="email" className="form-input" value={entraEditData.personalEmail}
                          onChange={(e) => setEntraEditData({...entraEditData, personalEmail: e.target.value})}
                          data-testid="entra-edit-personal-email-main" />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Facility Name (Attr 02)</label>
                        <input type="text" className="form-input" value={entraEditData.facilityName}
                          readOnly
                          style={{ background: "var(--slate-50)", color: "var(--slate-600)" }}
                          title="Auto-set from Office Location selection"
                          data-testid="entra-edit-facility-main" />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Manager (Attr 10)</label>
                        <input type="text" className="form-input" value={entraEditData.managerEmail}
                          onChange={(e) => setEntraEditData({...entraEditData, managerEmail: e.target.value})}
                          data-testid="entra-edit-manager-main" />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Start Date (Attr 15)</label>
                        <input type="text" className="form-input" value={entraEditData.startDate} placeholder="e.g., 01/15/2025"
                          onChange={(e) => setEntraEditData({...entraEditData, startDate: e.target.value})}
                          data-testid="entra-edit-startdate-main" />
                      </div>

                      {/* Effective Date of Change */}
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Effective Date of Change</label>
                        <input type="date" className="form-input" value={entraEditData.effectiveDate}
                          onChange={(e) => setEntraEditData({...entraEditData, effectiveDate: e.target.value})}
                          data-testid="entra-edit-effectivedate-main" />
                      </div>

                      {/* Company Code - Required */}
                      <div style={{ gridColumn: "1 / -1", border: !entraEditData.companyName?.trim() ? "1px solid #ef4444" : "1px solid var(--slate-200)", borderRadius: "6px", padding: "0.75rem", background: "var(--slate-50)" }}>
                        <label className="form-label" style={{ fontWeight: 600 }}>Company Code <span style={{ color: "red" }}>*</span></label>
                        <p style={{ fontSize: "0.8rem", color: "var(--slate-500)", margin: "0 0 0.5rem" }}>
                          Select the company code for this change.
                        </p>
                        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }} data-testid="entra-edit-company-main">
                          {["HGJ", "LUW", "HGP", "LVW", "LUF", "1GU"].map((code) => (
                            <button
                              key={code}
                              type="button"
                              onClick={() => setEntraEditData({...entraEditData, companyName: code})}
                              data-testid={`company-code-${code}`}
                              style={{
                                padding: "0.5rem 1.25rem", borderRadius: "6px", fontWeight: 600,
                                fontSize: "0.9rem", cursor: "pointer", transition: "all 0.15s",
                                border: entraEditData.companyName === code ? "2px solid var(--primary-600, #2563eb)" : "1px solid var(--slate-300)",
                                background: entraEditData.companyName === code ? "var(--primary-50, #eff6ff)" : "white",
                                color: entraEditData.companyName === code ? "var(--primary-700, #1d4ed8)" : "var(--slate-700)",
                                boxShadow: entraEditData.companyName === code ? "0 0 0 1px var(--primary-600, #2563eb)" : "none"
                              }}
                            >{code}</button>
                          ))}
                        </div>
                      </div>

                      {/* Additional Notification Recipients */}
                      <div style={{ gridColumn: "1 / -1", border: "1px solid var(--slate-200)", borderRadius: "6px", padding: "0.75rem", background: "var(--slate-50)" }}>
                        <label className="form-label" style={{ fontWeight: 600 }}>Additional Notification Recipients</label>
                        <p style={{ fontSize: "0.8rem", color: "var(--slate-500)", margin: "0 0 0.5rem" }}>
                          Add extra emails to notify for this specific change. Separate multiple addresses with a comma.
                        </p>
                        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
                          <input type="text" className="form-input" placeholder="email1@therapymgmt.com, email2@therapymgmt.com"
                            value={entraNewAdditionalEmail}
                            onChange={(e) => setEntraNewAdditionalEmail(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                const emails = entraNewAdditionalEmail.split(",").map(e => e.trim()).filter(e => e && e.includes("@"));
                                if (emails.length > 0) {
                                  setEntraAdditionalEmails(prev => [...prev, ...emails.filter(e => !prev.includes(e))]);
                                  setEntraNewAdditionalEmail("");
                                }
                              }
                            }}
                            style={{ flex: 1 }} data-testid="entra-additional-email-main" />
                          <button className="btn btn-secondary" type="button" style={{ fontSize: "0.85rem" }}
                            onClick={() => {
                              const emails = entraNewAdditionalEmail.split(",").map(e => e.trim()).filter(e => e && e.includes("@"));
                              if (emails.length > 0) {
                                setEntraAdditionalEmails(prev => [...prev, ...emails.filter(e => !prev.includes(e))]);
                                setEntraNewAdditionalEmail("");
                              }
                            }}>Add</button>
                        </div>
                        {entraAdditionalEmails.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                            {entraAdditionalEmails.map((email, idx) => (
                              <span key={idx} style={{
                                display: "inline-flex", alignItems: "center", gap: "0.3rem",
                                background: "var(--primary-100, #e0e7ff)", color: "var(--primary-700, #3730a3)",
                                padding: "0.2rem 0.6rem", borderRadius: "999px", fontSize: "0.85rem"
                              }}>
                                {email}
                                <button onClick={() => setEntraAdditionalEmails(prev => prev.filter((_, i) => i !== idx))}
                                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--primary-700)", fontWeight: "bold", fontSize: "1rem", padding: 0 }}>×</button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Comments (optional, free-form) - shown in the notification email if provided */}
                      <div style={{ gridColumn: "1 / -1", border: "1px solid var(--slate-200)", borderRadius: "6px", padding: "0.75rem", background: "var(--slate-50)" }}>
                        <label className="form-label" style={{ fontWeight: 600 }} htmlFor="entra-edit-comment-main">Comments <span style={{ fontWeight: 400, color: "var(--slate-500)", fontSize: "0.8rem" }}>(optional)</span></label>
                        <p style={{ fontSize: "0.8rem", color: "var(--slate-500)", margin: "0 0 0.5rem" }}>
                          Add any free-form notes about this change. If provided, the comment will be included in the notification email.
                        </p>
                        <textarea
                          id="entra-edit-comment-main"
                          className="form-input"
                          rows={3}
                          value={entraEditData.comment || ""}
                          onChange={(e) => setEntraEditData({...entraEditData, comment: e.target.value})}
                          placeholder="e.g., Reason for status change, additional context, follow-up actions..."
                          data-testid="entra-edit-comment-main"
                          style={{ width: "100%", resize: "vertical", fontFamily: "inherit" }}
                        />
                      </div>

                      <div style={{ gridColumn: "1 / -1", display: "flex", gap: "0.75rem" }}>
                        <button className="btn btn-primary" onClick={handleEntraUpdate} disabled={entraLoading} data-testid="entra-save-btn-main">
                          {entraLoading ? "Saving..." : "Save Changes"}
                        </button>
                        <button className="btn btn-secondary" onClick={handleEntraCancelEdit} data-testid="entra-cancel-btn-main">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {mainTab === "processed" && (
          <div className="card" data-testid="processed-tab">
            <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
              <h2 className="card-title">Processed Employees</h2>
              <input
                type="text"
                className="form-input"
                placeholder="Search by name, position ID, facility, or processed by..."
                value={processedSearch}
                onChange={(e) => setProcessedSearch(e.target.value)}
                data-testid="processed-search-input-main"
                style={{ maxWidth: "420px", flex: 1 }}
              />
            </div>
            <div className="card-body">
              {processedList.length === 0 ? (
                <div className="empty-state"><p>No processed employees yet.</p></div>
              ) : (() => {
                const q = processedSearch.trim().toLowerCase();
                const filtered = !q ? processedList : processedList.filter((emp) => {
                  const hay = [
                    emp.first_name, emp.last_name, emp.middle_name,
                    emp.position_id, emp.employee_status,
                    emp.home_facility, emp.facility_id,
                    emp.processed_by, emp.job_title,
                    emp.personal_email, emp.adp_code,
                  ].filter(Boolean).join(" ").toLowerCase();
                  return hay.includes(q);
                });
                return (
                  <>
                    <p style={{ fontSize: "0.85rem", color: "var(--slate-500)", margin: "0 0 0.75rem" }}>
                      Showing {filtered.length} of {processedList.length} processed employee{processedList.length === 1 ? "" : "s"}
                    </p>
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th style={{ width: "44px" }}></th>
                            <th>Position ID</th>
                            <th>Name</th>
                            <th>Status</th>
                            <th>Facility</th>
                            <th>Processed By</th>
                            <th>Processed At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.map((emp) => (
                            <tr key={emp.id}>
                              <td>
                                <button
                                  type="button"
                                  title="View all submitted details"
                                  onClick={() => { setViewingProcessedEmp(emp); setRevealPassword(false); }}
                                  data-testid={`view-processed-emp-${emp.id}`}
                                  style={{
                                    background: "transparent", border: "none", cursor: "pointer",
                                    padding: "0.3rem", borderRadius: "4px", color: "var(--primary-600)",
                                    display: "inline-flex", alignItems: "center", justifyContent: "center"
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = "var(--primary-50)"}
                                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                                >
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                  </svg>
                                </button>
                              </td>
                              <td>{emp.position_id}</td>
                              <td>{emp.first_name} {emp.last_name}</td>
                              <td><span className="badge badge-info">{emp.employee_status}</span></td>
                              <td>{emp.home_facility || "-"}</td>
                              <td>{emp.processed_by || "-"}</td>
                              <td>{emp.processed_at ? new Date(emp.processed_at).toLocaleDateString() : "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Processed Employee Detail Modal */}
        {viewingProcessedEmp && (() => {
          const emp = viewingProcessedEmp;
          const HIDDEN_KEYS = new Set([
            "id", "_id",
            "submission_locked", "entra_check_performed",
            "temporary_password",  // shown separately in its own section with masking
          ]);
          const HUMANIZE = (k) => k
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase())
            .replace(/Adp/g, "ADP")
            .replace(/Tcn/g, "TCN")
            .replace(/Hr /g, "HR ")
            .replace(/Id$/, "ID")
            .replace(/Id /g, "ID ");
          const sections = [
            { title: "Identity", keys: ["position_id", "first_name", "middle_name", "last_name", "employee_status", "is_a_tcn", "discipline", "designation"] },
            { title: "Contact", keys: ["personal_email", "home_phone_number", "birth_date"] },
            { title: "Position", keys: ["position_start_date", "pay_group", "job_title", "employee_type", "roles", "ops_admin", "clock_in_out", "adp_code", "hr_agent"] },
            { title: "Facility", keys: ["home_facility", "facility_id", "additional_facilities"] },
            { title: "License", keys: ["license_state", "license_number", "license_issue_date", "license_expiration"] },
            { title: "Notifications", keys: ["manager_email", "director_of_rehabilitation_email", "facility_email"] },
            { title: "Custom / Notes", keys: ["custom_field_1", "custom_field_2", "notes"] },
            { title: "Processing", keys: ["processed", "processed_at", "processed_by", "submitted_at", "submission_status", "submission_error", "entra_original_first_name", "temporary_password_received_at", "created_at", "updated_at"] },
          ];
          const seen = new Set(["id", "_id", "temporary_password"]);
          sections.forEach((s) => s.keys.forEach((k) => seen.add(k)));
          const extraKeys = Object.keys(emp).filter((k) => !seen.has(k) && !HIDDEN_KEYS.has(k));
          if (extraKeys.length) sections.push({ title: "Other", keys: extraKeys });
          const renderValue = (v) => {
            if (v === null || v === undefined || v === "") return <span style={{ color: "var(--slate-400)" }}>—</span>;
            if (typeof v === "boolean") return v ? "Yes" : "No";
            if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
              try { return new Date(v).toLocaleString(); } catch { return v; }
            }
            return String(v);
          };
          const tempPw = emp.temporary_password;

          return (
            <div
              className="modal-overlay"
              onClick={() => { setViewingProcessedEmp(null); setRevealPassword(false); }}
              data-testid="view-processed-modal-main"
            >
              <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "860px", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
                <div className="modal-header">
                  <h3 className="modal-title">
                    {emp.first_name} {emp.last_name}
                    <span style={{ fontSize: "0.85rem", color: "var(--slate-500)", marginLeft: "0.75rem", fontWeight: 400 }}>
                      Position {emp.position_id || "—"}
                    </span>
                  </h3>
                  <button className="modal-close" onClick={() => { setViewingProcessedEmp(null); setRevealPassword(false); }}>×</button>
                </div>
                <div className="modal-body" style={{ overflow: "auto", flex: 1 }}>
                  {/* Temporary Password — always at the top, masked by default */}
                  <div style={{
                    marginBottom: "1.25rem",
                    border: "1px solid var(--amber-300, #fcd34d)",
                    background: "var(--amber-50, #fffbeb)",
                    borderRadius: "8px",
                    padding: "0.85rem 1rem"
                  }}>
                    <h4 style={{
                      margin: "0 0 0.4rem", fontSize: "0.78rem", fontWeight: 700,
                      textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--amber-700, #b45309)"
                    }}>
                      Temporary Password
                    </h4>
                    {tempPw ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                        <code
                          data-testid="processed-temp-password"
                          style={{
                            fontSize: "1rem", letterSpacing: revealPassword ? "0.04em" : "0.2em",
                            background: "white", padding: "0.4rem 0.7rem", borderRadius: "4px",
                            border: "1px solid var(--slate-300)", color: "var(--slate-900)",
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                            userSelect: revealPassword ? "all" : "none"
                          }}
                        >
                          {revealPassword ? tempPw : "•".repeat(Math.min(Math.max(tempPw.length, 8), 16))}
                        </code>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setRevealPassword((r) => !r)}
                          data-testid="processed-temp-password-toggle"
                        >
                          {revealPassword ? "Hide" : "Show"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => {
                            try {
                              navigator.clipboard.writeText(tempPw);
                              toast.success("Password copied to clipboard");
                            } catch {
                              toast.error("Copy failed — browser blocked clipboard access");
                            }
                          }}
                          data-testid="processed-temp-password-copy"
                        >
                          Copy
                        </button>
                      </div>
                    ) : (
                      <p style={{ margin: 0, color: "var(--slate-600)", fontSize: "0.88rem" }}>
                        No temporary password recorded for this employee. (Power Automate may not be returning it yet.)
                      </p>
                    )}
                  </div>

                  {sections.map((section) => {
                    const rows = section.keys
                      .filter((k) => k in emp && !HIDDEN_KEYS.has(k))
                      .map((k) => ({ k, v: emp[k] }));
                    if (!rows.length) return null;
                    return (
                      <div key={section.title} style={{ marginBottom: "1.25rem" }}>
                        <h4 style={{
                          margin: "0 0 0.5rem", fontSize: "0.78rem", fontWeight: 700,
                          textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--slate-500)",
                          borderBottom: "1px solid var(--slate-200)", paddingBottom: "0.3rem"
                        }}>
                          {section.title}
                        </h4>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                          <tbody>
                            {rows.map(({ k, v }) => (
                              <tr key={k}>
                                <td style={{ padding: "0.35rem 0.6rem", fontWeight: 600, color: "var(--slate-700)", verticalAlign: "top", width: "240px" }}>
                                  {HUMANIZE(k)}
                                </td>
                                <td style={{ padding: "0.35rem 0.6rem", color: "var(--slate-900)", wordBreak: "break-word" }}>
                                  {renderValue(v)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-primary" onClick={() => { setViewingProcessedEmp(null); setRevealPassword(false); }}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </main>
      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} />
    </div>
  );
};

export default MainPage;
